import "./App.css";
import { Layout } from "./common/components/layout/Layout";
import { useAppContext } from "./AppContext";
import { matchPath, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import {
  getDefaultRoutePath,
  routePaths,
} from "./common/infrastructure/routes/routePaths";
import { HeadProvider } from "react-head";
import { useGoogleAuthReturn } from "./common/infrastructure/hooks/useGoogleAuthReturn";
import { setupLocales } from "./common/infrastructure/config/localeConfig";
// Disabled for now in favour of the draggable floating back button.
// import { useScrollLeftForBack } from "./common/infrastructure/hooks/useScrollLeftForBack";
import { useScrollToTop } from "./common/infrastructure/hooks/useScrollToTop";
import { useNotificationClickNavigation } from "./common/infrastructure/hooks/useNotificationClickNavigation";
import { CookieConsentProvider } from "./common/components/cookieConsent/CookieConsentProvider";
import { useVersionCheck } from "./common/infrastructure/hooks/useVersionCheck";
import { useFontSizePreference } from "./common/infrastructure/hooks/useFontSizePreference";
import { OnboardingProvider } from "./components/onboarding/OnboardingProvider";
import { OnboardingSetupGuide } from "./components/onboarding/OnboardingSetupGuide";
import { OnboardingOverlay } from "./components/onboarding/OnboardingOverlay";
import { RestTimerProvider } from "./common/components/workoutPlanDialog/RestTimerContext";
import { PrivateRoutesOutlet } from "./common/infrastructure/routes/components/PrivateRoutesOutlet";

function App() {
  const { privateRoutes, publicRoutes, isAuthenticated, currentUser } =
    useAppContext();
  const location = useLocation();

  // Version check hook - automatically reloads when new version detected
  useVersionCheck();

  // Apply user's font size preference
  useFontSizePreference(currentUser);

  // Handle Google authentication return flow
  useGoogleAuthReturn();
  setupLocales();
  // Left-edge swipe-to-go-back gesture (with the "←" indicator) disabled for
  // now; the draggable floating back button covers this instead.
  // useScrollLeftForBack();
  useScrollToTop();

  // A tapped notification opens its link here (see public/service-worker.js).
  useNotificationClickNavigation();

  let defaultRoute = "";

  if (isAuthenticated) {
    defaultRoute = getDefaultRoutePath(currentUser);
  } else {
    defaultRoute = routePaths.portalAppointment;
  }

  // Standalone public pages (e.g. a studio's shared link page) render on their own, before
  // the Layout is ever mounted: whoever opens one is usually not a logged-in user of that
  // studio, so the app chrome, the studio-picker / phone-number dialogs and the theming
  // taken from the *selected* studio would all be wrong there.
  const standaloneRoute = publicRoutes.find(
    (route) => route.isStandalone && matchPath(route.path, location.pathname),
  );

  if (standaloneRoute) {
    return (
      <HeadProvider>
        <Routes>
          <Route
            path={standaloneRoute.path}
            element={standaloneRoute.element}
          />
        </Routes>
      </HeadProvider>
    );
  }

  return (
    <HeadProvider>
      <CookieConsentProvider>
        <OnboardingProvider>
          <RestTimerProvider>
            <Layout className="App">
              <Routes>
                {publicRoutes.map(({ path, element }) => (
                  <Route key={path} path={path} element={element} />
                ))}
                {/* A logged-out visitor goes to /login and comes back to the page after logging in. */}
                <Route element={<PrivateRoutesOutlet fallbackPath={defaultRoute} />}>
                  {privateRoutes.map(({ path, element }) => (
                    <Route key={path} path={path} element={element} />
                  ))}
                </Route>

                <Route path="*" element={<Navigate to={defaultRoute} replace />} />
              </Routes>
            </Layout>
            <OnboardingSetupGuide />
            <OnboardingOverlay />
          </RestTimerProvider>
        </OnboardingProvider>
      </CookieConsentProvider>
    </HeadProvider>
  );
}

export default App;
